/*
 * color-manager-pdp.js (Phase 2 Task 4)
 * Glue between the colour-manager component (select mode) and the product page.
 * Opens/closes the drawer, and on a `cm:select` event:
 *   - updates the trigger label + the chosen-colour box near the buy box,
 *   - injects line_item.properties[Farge] / [Stoff] / [Stoffgruppe] into the
 *     product form so add-to-cart carries the choice to the cart line.
 * No price faking: it only displays the chosen colour's Stoffgruppe.
 *
 * 2026-08-31 — the choice is now MANDATORY (data-cm-required on [data-cm-pdp]).
 * Until a colour is picked the buy button is locked and submit is blocked, so an
 * order can never reach fulfilment without a fabric + colour. The button is NOT
 * [disabled]: a disabled button emits no click, so the shopper would get no
 * explanation — same pattern as the cart terms gate.
 */
(function () {
  'use strict';

  function boot() {
    var drawer = document.querySelector('[data-cm-drawer]');
    if (!drawer || drawer.dataset.cmPdpReady) return;
    drawer.dataset.cmPdpReady = '1';

    var form = document.querySelector('.m-buy__form') || document.querySelector('form[action*="/cart/add"]');
    var chosenLabel = document.querySelector('[data-cm-chosen]');
    var chosenBox = document.querySelector('[data-cm-pdp-chosen]');
    var chosenImg = document.querySelector('[data-cm-chosen-img]');
    var chosenName = document.querySelector('[data-cm-chosen-name]');
    var chosenGroup = document.querySelector('[data-cm-chosen-group]');
    // If the product has a Stoffgruppe variant axis, the colour selector drives
    // it — hide the raw pill picker. (Absent on products that can't add it, e.g.
    // ones already using all 3 Shopify option slots — they just show the note.)
    var sgGroup = document.querySelector('[data-option][data-option-name="Stoffgruppe"]');
    if (sgGroup) sgGroup.style.display = 'none';

    function open() {
      drawer.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var s = drawer.querySelector('[data-cm="search"]');
      if (s) setTimeout(function () { s.focus(); }, 50);
    }
    function close() {
      drawer.hidden = true;
      document.documentElement.style.overflow = '';
    }
    document.querySelectorAll('[data-cm-pdp-trigger]').forEach(function (b) { b.addEventListener('click', open); });
    drawer.querySelectorAll('[data-cm-drawer-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !drawer.hidden) close(); });

    function setProp(name, value) {
      if (!form) return;
      var inp = form.querySelector('input[name="properties[' + name + ']"]');
      if (!inp) {
        inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = 'properties[' + name + ']';
        form.appendChild(inp);
      }
      inp.value = value;
    }

    var pdpRoot = document.querySelector('[data-cm-pdp]');
    var required = !!(pdpRoot && pdpRoot.getAttribute('data-cm-required'));
    var errEl = document.querySelector('[data-cm-error]');
    var hintEl = document.querySelector('[data-cm-hint]');
    var addBtn = document.querySelector('[data-add-btn]');
    var chosen = false;

    if (!required) {
      // Legacy behaviour: choosing a textile is OPTIONAL. "Ikke valgt" means the
      // customer accepts the configuration shown in the product photos. State that
      // on the cart line up front so an order without a colour pick is unambiguous
      // for fulfilment instead of carrying no fabric at all. A later cm:select
      // simply overwrites it with the real choice.
      var defaultFabric = (pdpRoot && pdpRoot.getAttribute('data-cm-default-label')) || 'Som på bildet';
      setProp('Stoff', defaultFabric);
    }

    function lock() {
      if (!required || !addBtn) return;
      addBtn.classList.toggle('is-locked', !chosen);
      addBtn.setAttribute('aria-describedby', chosen ? '' : 'cm-pdp-hint');
      if (hintEl) hintEl.hidden = chosen;
    }
    function complain() {
      if (errEl) errEl.hidden = false;
      if (pdpRoot) pdpRoot.classList.add('is-error');
      // Open FIRST: scrolling is a nicety, and a browser without smooth
      // scrollIntoView must not cost the shopper the picker itself.
      open();
      try { if (pdpRoot) pdpRoot.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
    }

    if (required && form) {
      // Older browsers have no event.submitter; remember the last submit pressed.
      var lastBtn = null;
      form.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('button[type="submit"], input[type="submit"]');
        if (b) lastBtn = b;
      }, true);
      form.addEventListener('submit', function (e) {
        if (chosen) return;
        var sub = e.submitter || lastBtn;
        // Only the buy button is gated — never a future secondary submit.
        if (sub && sub !== addBtn && !sub.hasAttribute('data-add-btn')) return;
        e.preventDefault();
        e.stopPropagation();
        complain();
      }, true);
      // A locked button still fires click (it is not [disabled]) — explain there too,
      // for browsers that swallow the submit event on a novalidate form.
      if (addBtn) addBtn.addEventListener('click', function () { if (!chosen) complain(); });
      lock();
    }

    document.addEventListener('cm:select', function (e) {
      var c = e.detail && e.detail.colour, coll = e.detail && e.detail.collection;
      if (!c || !coll) return;
      if (chosenLabel) chosenLabel.textContent = c.name;
      if (chosenBox) chosenBox.hidden = false;
      if (chosenImg && c.swatch) chosenImg.src = c.swatch;
      if (chosenName) chosenName.textContent = c.name;
      // The label states the group and nothing else. Owner confirmed 2026-08-31
      // that every fabric costs the same, so promising a "pristillegg" here was
      // wrong; where a Stoffgruppe variant IS priced differently the theme's own
      // price element updates below, which is the only honest source.
      if (chosenGroup) chosenGroup.textContent = coll.price_group
        ? 'Stoffgruppe ' + coll.price_group
        : '';
      // Drive the Stoffgruppe variant -> native price update (price lives on the
      // variant, never hardcoded here). Resolve price_group -> "Stoffgruppe N"
      // -> check that radio -> the theme's own change handler matches the
      // variant and updates the price + variant id.
      if (sgGroup && coll.price_group) {
        // The theme's option radios share no `name`, so they don't auto-uncheck;
        // uncheck the whole group first, then check the target, so the theme's
        // selectedValues() (input:checked) resolves to our Stoffgruppe.
        var radios = sgGroup.querySelectorAll('input[type="radio"]');
        for (var i = 0; i < radios.length; i++) radios[i].checked = false;
        var target = sgGroup.querySelector('input[value="Stoffgruppe ' + coll.price_group + '"]');
        if (target) {
          target.checked = true;
          target.dispatchEvent(new Event('input', { bubbles: true }));
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      // line_item.properties for the cart line
      setProp('Stoff', coll.name);     // collection (fabric)
      setProp('Farge', c.name);        // colour
      setProp('Stoffgruppe', coll.price_group || '');
      chosen = true;
      if (errEl) errEl.hidden = true;
      if (pdpRoot) pdpRoot.classList.remove('is-error');
      lock();
      close();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
